import { useState, useEffect } from 'react';
import {
  TIER_NAMES,
  COMMITMENT_LABELS,
  getCataloguePricing,
  displayPrice,
  fmtUsd,
  type CommitmentTerm,
} from '../../lib/promo-codes';
import type { StepProps } from '../OBWWizard';
import { portalApi } from '../../lib/portal-api';
import { fetchCatalogue, type Catalogue } from '../../lib/catalogue';
import type { PaymentRecovery } from '../payment';

// Phase 5c.5 (ADR-030 D2 Step 4) — plan summary panel on left, Stripe-element
// stub on right. Promo math displayed if applied. Real Stripe Elements arrive
// post-Phase-6 — this step renders a card-styled stub that gates the
// "Continue" CTA.

type Tier = 'T1' | 'T2' | 'T3';

interface PaymentPreviewResponse {
  tier: string;
  basePrice: number;
  discountedPrice: number;
  promoCode?: string;
  promoDescription?: string;
  note?: string;
}

export function StepPayment({ data, accountId, onNext, onJumpToStep, payment, presentation }: StepProps) {
  const compact = presentation === 'embedded';
  const tier = (data?.selectedTier as Tier) ?? null;
  const promoCode = (data?.promoCode as string) ?? null;
  const commitment = (data?.commitment as CommitmentTerm) ?? 'annual';
  /**
   * ADR-073 Am.5 §A5.6 — the forward button's words, from the MOUNT's payment port (data, never a
   * journey sniff — L5). On the public sign-up this button IS the commercial commitment (the account
   * is created when it confirms), so the mount says so ("Pay and create my account"); every other
   * mount keeps "Continue", because there the next step is still part of an existing account's setup.
   */
  const ctaLabel = payment?.cta || 'Continue';
  /** The mount's plain-words refusal of a confirm, and the way forward it offers. */
  const [refusal, setRefusal] = useState<{ message: string; recover?: PaymentRecovery } | null>(null);

  const [preview, setPreview] = useState<PaymentPreviewResponse | null>(null);
  const [catalogue, setCatalogue] = useState<Catalogue | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetchCatalogue()
      .then((cat) => { if (!cancelled) setCatalogue(cat); })
      .catch(() => { /* fail-open: the static table (ADR-085) */ });
    return () => { cancelled = true; };
  }, []);
  const [previewError, setPreviewError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // PL15 — record the stub card on file before advancing. The card was
  // previously discarded (onNext only), so it never showed up in Billing /
  // Manage Account and second-domain journeys re-collected payment. Persisting
  // it here (best-effort: an account must exist, so sign-up's pre-account payment
  // step simply skips the POST and continues) makes the card show as on-file and
  // lets later domains elide the payment step (SetupPlanAndPage skipSteps).
  async function recordCardAndContinue() {
    if (submitting) return;
    setSubmitting(true);
    setRefusal(null);
    // ⭐ ADR-073 Am.5 §A5.6 — a mount on which this button IS the commitment confirms through its port
    // (the public sign-up: payment-confirm creates the login, account, domain and subscription). The
    // step advances only when the mount says it confirmed; a refusal stays here, in plain words, with
    // the way forward the mount named (e.g. back to Domain Authorization for a taken website).
    if (payment) {
      let outcome: Awaited<ReturnType<typeof payment.confirm>>;
      try {
        outcome = await payment.confirm(data ?? {});
      } catch {
        outcome = { ok: false, message: 'Network error. Please check your connection and try again. You haven’t been charged.' };
      }
      setSubmitting(false);
      if (!outcome.ok) {
        setRefusal({ message: outcome.message, recover: outcome.recover });
        return;
      }
      onNext({ paymentStubbed: true, cardOnFile: true });
      return;
    }
    if (accountId) {
      try {
        // ⛔ THIS WRITE 401'd FOR ITS ENTIRE LIFE (ADR-107 §1.2, found by the
        // authenticated E2E walk). It used `credentials: 'include'` with no Bearer
        // against a route behind the Cognito client authorizer — so PL15's whole
        // point (the card shows as on-file, and later domains ELIDE the payment
        // step via skipSteps) never once happened. `portalApi` attaches the token.
        await portalApi(`/portal/accounts/${accountId}/billing/payment-method`, {
          method: 'POST',
          body: JSON.stringify({ brand: 'card', last4: '4242' }),
        });
      } catch {
        // Non-fatal — the stub card is display-only; never block setup on it.
      }
    }
    setSubmitting(false);
    onNext({ paymentStubbed: true, cardOnFile: true });
  }

  // Promo-description lookup only — headline numbers come from the commitment
  // pricing model below (display-only; real Stripe is LAUNCH/5d-4).
  //
  // ⭐ THE RED "Could not load price preview" IS SUPPRESSED — DELIBERATELY, and this
  // is the whole fix for it. What this call fetches is ONE COSMETIC STRING
  // (`promoDescription`). Every number the user reads — $99/mo, billed $1188 — is
  // computed locally by `getCommitmentPricing` below and renders whether or not this
  // call succeeds. That is why the price appeared correct beside a red error: the
  // error never described the price at all. Alarming a paying customer mid-checkout
  // about a decorative lookup is worse than the missing decoration.
  //
  // ⚠ AND THE CALL IS DEAD BY CONSTRUCTION — three independent mismatches against
  // `/portal/payment/preview` (lambdas/billing/src/billing-handlers.ts:447), which
  // self-declares as `note: 'Phase 5b stub pricing — Stripe integration in Phase 6'`:
  //   1. 400 — the handler requires `billingCycle`; this body never sends it.
  //   2. 401 — the route sits behind `clientAuthorizer` (Bearer idToken), but this is a
  //      raw fetch with only `credentials: 'include'`. Same defect class already fixed
  //      in OnboardPage/DashboardPage by routing through `portalApi()`.
  //   3. Shape — the handler returns `discountDescription`; the client reads
  //      `promoDescription`, so even a 200 would set `undefined`.
  // Not repaired here ON PURPOSE: all three are contract bugs against a stub that
  // Phase 6 replaces with Stripe, and "fixing" them now would mean writing a client to
  // match a response shape that is about to change. Recorded so the Stripe build fixes
  // them properly rather than rediscovering them. `previewError` is retained (unset) so
  // that build has the surface back when there is a real failure worth reporting.
  useEffect(() => {
    // ⚠ NOT ON AN ACCOUNT-LESS MOUNT (ADR-073 Am.5 — the public sign-up's payment step runs before
    // any account exists). The route is authenticated (`clientAuthorizer`), there is no session to
    // send, and the call fetches one decorative string — so it is not made at all, rather than made
    // to fail. Every number on this page is computed locally either way.
    //
    // ⚠ NOR ON A MOUNT WHOSE PAYMENT PORT IS THE COMMITMENT (ADR-073 Am.5 §A5.6). There the account id
    // arrives the instant payment confirms — while this step is still on screen — and the visitor
    // still has no session, so the call would go out unauthenticated (found by the walk test).
    if (!tier || !accountId || payment) return;
    let cancelled = false;
    setPreviewError('');
    void (async () => {
      try {
        // ⛔ ALSO 401'd — same shape, same cause. The priced summary (term,
        // discount, promo) has never rendered from the server for any subscriber;
        // the page has been showing its fallback the whole time.
        const { status, data } = await portalApi<PaymentPreviewResponse>('/portal/payment/preview', {
          method: 'POST',
          body: JSON.stringify({ accountId, tier, promoCode }),
          allowedStatuses: [400, 404, 500, 502],
        });
        if (status === 200 && data && !cancelled) {
          setPreview(data);
        }
      } catch {
        // Swallowed to match the non-ok path two lines up, which has always been
        // silent. A thrown fetch (CORS/preflight/network) is not more user-actionable
        // than a 400 — and neither withholds a single number from the page.
      }
    })();
    return () => { cancelled = true; };
  }, [accountId, tier, promoCode, payment]);

  // ADR-085 — the live catalogue price, exactly as the plan step showed it (one shared
  // calculation: lib/promo-codes `displayPrice`); the static table only until it resolves.
  // ⛔ No fallback price (promo-codes.ts): until the catalogue loads the summary says so.
  const pricing = tier && catalogue ? getCataloguePricing(catalogue, tier, commitment) : null;
  const tierName = tier ? TIER_NAMES[tier] : null;
  // D8 — no local promo math: the promo capability is not built, and a code from the local table
  // previewed a discount the server never billed. Only the catalogue's (server-resolved) prices show.
  const promoDiscount = 0;
  const shown = pricing ? displayPrice(pricing, promoDiscount) : null;
  const effectiveMonthly = fmtUsd(shown?.effectiveMonthly ?? 0);
  const upfrontTotal = fmtUsd(shown?.termTotal ?? 0);
  const showStrike = !!shown?.showStrike;

  if (!tier) {
    return (
      <div className="obw-step-content">
        <h2 className="obw-title">Payment</h2>
        <p className="obw-subtitle">No plan selected. Go back and choose a plan to continue.</p>
      </div>
    );
  }

  const summaryLine = commitment === 'monthly'
    ? `Billed $${effectiveMonthly} each month`
    : `Billed $${upfrontTotal} once for ${pricing?.months} months`;

  return (
    <div className={compact ? 'obw-step-content obw-payment-compact' : 'obw-step-content'}>
      <h2 className="obw-title">Payment</h2>
      <p className="obw-subtitle">
        {compact
          ? 'We use Stripe — your card details never reach Nexanomics.'
          : 'Add a card to activate your subscription. We use Stripe — your card details are never sent to or stored on Nexanomics servers.'}
      </p>

      <div className={compact ? 'obw-payment-grid obw-payment-grid-compact' : 'obw-payment-grid'}>
        {/* Order summary */}
        <div className="obw-auth-box obw-payment-box" data-testid="obw-order-summary" aria-label="Order summary">
          {!compact && <p className="obw-form-section">Order summary</p>}
          {!pricing ? (
            <p className="obw-payment-meta" role="status" aria-live="polite">Loading your price…</p>
          ) : (
            <>
              <p className="obw-payment-plan">
                {tierName} <span className="obw-payment-meta">· {COMMITMENT_LABELS[commitment]}{pricing.isEa ? ' · Early Adopter' : ''}</span>
              </p>
              <div className="obw-payment-price-line">
                <p className="obw-payment-price">
                  {showStrike && <span className="obw-payment-strike">${fmtUsd(pricing.rackMonthly)}</span>}
                  ${effectiveMonthly}<span className="obw-payment-meta">/mo</span>
                </p>
                <p className="obw-payment-meta">{summaryLine}</p>
              </div>
              {pricing.isEa && (
                <p className="obw-payment-note">✓ Renews at ${fmtUsd(pricing.rackMonthly)}/mo (rack rate) after the committed term</p>
              )}
              {preview?.promoDescription && (
                <p className="obw-payment-note">✓ {preview.promoDescription}</p>
              )}
            </>
          )}
          {previewError && <p className="obw-payment-error">{previewError}</p>}
        </div>

        {/* Stripe element stub */}
        <div className="obw-auth-box obw-payment-box" aria-label="Card details">
          <p className="obw-form-section">Card details</p>
          {compact ? (
            // One row in the frame: number, expiry, CVC — each still labelled for assistive tech.
            <div className="obw-card-row">
              <input id="stub-card-num" className="obw-form-input" type="text" placeholder="Card number" disabled aria-label="Card number" aria-describedby="stripe-stub-note" />
              <input id="stub-card-exp" className="obw-form-input" type="text" placeholder="MM/YY" disabled aria-label="Expiry" />
              <input id="stub-card-cvc" className="obw-form-input" type="text" placeholder="CVC" disabled aria-label="CVC" />
            </div>
          ) : (
          <>
          <div className="obw-form-group">
            <label className="obw-form-label" htmlFor="stub-card-num">Card number</label>
            <input
              id="stub-card-num"
              className="obw-form-input"
              type="text"
              placeholder="4242 4242 4242 4242"
              disabled
              aria-describedby="stripe-stub-note"
            />
          </div>
          <div className="obw-form-grid-2">
            <div className="obw-form-group">
              <label className="obw-form-label" htmlFor="stub-card-exp">Expiry</label>
              <input id="stub-card-exp" className="obw-form-input" type="text" placeholder="MM/YY" disabled />
            </div>
            <div className="obw-form-group">
              <label className="obw-form-label" htmlFor="stub-card-cvc">CVC</label>
              <input id="stub-card-cvc" className="obw-form-input" type="text" placeholder="123" disabled />
            </div>
          </div>
          </>
          )}
          <p id="stripe-stub-note" className="obw-form-hint">
            Card payments are not switched on yet — your card will not be charged.
          </p>
        </div>
      </div>

      {refusal && (
        <div className="portal-wizard-error" role="alert" data-testid="obw-payment-refused">
          {refusal.message}
          {refusal.recover === 'domain-verify' && (
            <div className="obw-payment-recover">
              <button type="button" className="obw-btn-secondary" onClick={() => onJumpToStep('domain-verify')}>
                Use a different website
              </button>
            </div>
          )}
          {refusal.recover === 'sign-in' && payment?.signIn && (
            <div className="obw-payment-recover">
              <button type="button" className="obw-btn-secondary" onClick={payment.signIn}>Sign in instead</button>
            </div>
          )}
          {refusal.recover === 'restart' && payment?.restart && (
            <div className="obw-payment-recover">
              <button type="button" className="obw-btn-secondary" onClick={payment.restart}>Start again</button>
            </div>
          )}
        </div>
      )}

      <div className="portal-wizard-nav">
        <button
          type="button"
          className="obw-btn-primary"
          onClick={() => void recordCardAndContinue()}
          disabled={!tier || !pricing || submitting}
        >
          {submitting ? (payment ? 'Confirming…' : 'Saving…') : ctaLabel}
        </button>
      </div>
    </div>
  );
}
